import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _xcss2;
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import Popup from '@atlaskit/popup';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { media, UNSAFE_useMediaQuery as useMediaQuery } from '@atlaskit/primitives/responsive';
import { OverflowProvider } from '../../controllers/overflow';
import { PrimaryDropdownButton } from '../PrimaryDropdownButton';
var containerWrapperStyles = xcss({
  height: '100%'
});
var sharedContainerStyles = xcss({
  height: '100%',
  alignItems: 'stretch',
  paddingInlineEnd: 'space.050',
  gap: 'space.100'
});
var smallContainerStyles = xcss(_defineProperty({}, media.above.sm, {
  display: 'none'
}));
var mediumContainerStyles = xcss((_xcss2 = {
  display: 'none'
}, _defineProperty(_xcss2, media.above.sm, {
  display: 'flex'
}), _defineProperty(_xcss2, media.above.lg, {
  display: 'none'
}), _xcss2));
var largeContainerStyles = xcss(_defineProperty({
  display: 'none'
}, media.above.lg, {
  display: 'flex'
}));
var overflowItemsStyles = xcss({
  color: 'color.text'
});
var MoreItemsPopup = function MoreItemsPopup(_ref) {
  var moreLabel = _ref.moreLabel,
    testId = _ref.testId,
    items = _ref.items;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isMoreOpen = _useState2[0],
    setIsMoreOpen = _useState2[1];
  var onMoreClose = useCallback(function () {
    return setIsMoreOpen(false);
  }, []);
  var onMoreClick = useCallback(function () {
    return setIsMoreOpen(function (current) {
      return !current;
    });
  }, []);
  var openOverflowMenu = useCallback(function () {
    return setIsMoreOpen(true);
  }, []);
  var trigger = useCallback(function (triggerProps) {
    return /*#__PURE__*/React.createElement(PrimaryDropdownButton, _extends({
      onClick: onMoreClick,
      isSelected: isMoreOpen,
      testId: testId ? "".concat(testId, "-overflow-menu-trigger") : 'overflow-menu-trigger'
    }, triggerProps), moreLabel);
  }, [moreLabel, onMoreClick, isMoreOpen, testId]);
  var content = useCallback(function () {
    return /*#__PURE__*/React.createElement(OverflowProvider, {
      isVisible: false,
      openOverflowMenu: openOverflowMenu,
      closeOverflowMenu: onMoreClose
    }, /*#__PURE__*/React.createElement(Box, {
      xcss: overflowItemsStyles
    }, items));
  }, [items, openOverflowMenu, onMoreClose]);
  return /*#__PURE__*/React.createElement(Popup, {
    shouldRenderToParent: true,
    placement: "bottom-start",
    isOpen: isMoreOpen,
    onClose: onMoreClose,
    trigger: trigger,
    content: content,
    testId: testId ? "".concat(testId, "-overflow-menu-popup") : 'overflow-menu-popup'
  });
};

// Internal only
// eslint-disable-next-line @repo/internal/react/require-jsdoc
export var PrimaryItemsContainer = /*#__PURE__*/memo(function (_ref2) {
  var moreLabel = _ref2.moreLabel,
    items = _ref2.items,
    Create = _ref2.create,
    theme = _ref2.theme,
    testId = _ref2.testId;
  // We render a CSS media query based nav at first to handle SSR, then use
  // our useMediaQuery hook once we are hydrated so there is only one set of nav items
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isClient = _useState4[0],
    setIsClient = _useState4[1];
  useEffect(function () {
    setIsClient(true);
  }, []);

  // Setting up our media queries to use once app is hydrated
  var mqSm = useMediaQuery('above.sm', function (event) {
    return setIsAboveSm(event.matches);
  });
  var _useState5 = useState(mqSm === null || mqSm === void 0 ? void 0 : mqSm.matches),
    _useState6 = _slicedToArray(_useState5, 2),
    isAboveSm = _useState6[0],
    setIsAboveSm = _useState6[1];
  var mqLg = useMediaQuery('above.lg', function (event) {
    return setIsAboveLg(event.matches);
  });
  var _useState7 = useState(mqLg === null || mqLg === void 0 ? void 0 : mqLg.matches),
    _useState8 = _slicedToArray(_useState7, 2),
    isAboveLg = _useState8[0],
    setIsAboveLg = _useState8[1];

  // Filter out any falsy items passed in
  var filteredItems = useMemo(function () {
    return React.Children.toArray(items).filter(function (item) {
      return !!item;
    });
  }, [items]);
  // NOTE: we could make these max items configurable in the future. For now
  // we are using sensible defaults. Anything over the max gets put in the
  // overflow menu for that screen size.
  // While it may be tempting to use more than 3 items for the medium nav when
  // you see the available space, you need to consider internationalistion.
  // 3 is the safe, defensive choice across many languages and scripts.
  var smallMaxItems = 0;
  var mediumMaxItems = 3;
  var largeMaxItems = 8;

  // We re-use this in both the CSS media query nav that loads in for SSR,
  // and the JS/hook media query nav that is used once hydrated
  var navItems = useMemo(function () {
    return {
      small: {
        navBarItems: [],
        overflowItems: filteredItems
      },
      medium: {
        navBarItems: filteredItems.slice(0, mediumMaxItems),
        overflowItems: filteredItems.slice(mediumMaxItems, filteredItems.length)
      },
      large: {
        navBarItems: filteredItems.slice(0, largeMaxItems),
        overflowItems: filteredItems.slice(largeMaxItems, filteredItems.length)
      }
    };
  }, [filteredItems]);
  var hydratedNavItems = useMemo(function () {
    if (isAboveLg) {
      return navItems.large;
    }
    if (isAboveSm && !isAboveLg) {
      return navItems.medium;
    }
    return navItems.small;
  }, [isAboveLg, isAboveSm, navItems.large, navItems.medium, navItems.small]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, isClient ? /*#__PURE__*/React.createElement(Box, {
    role: "list",
    xcss: containerWrapperStyles
  }, /*#__PURE__*/React.createElement(Inline, {
    testId: testId && "".concat(testId, "-primary-actions"),
    xcss: sharedContainerStyles
  }, hydratedNavItems.navBarItems, hydratedNavItems.overflowItems.length > 0 && /*#__PURE__*/React.createElement(MoreItemsPopup, {
    moreLabel: moreLabel,
    items: hydratedNavItems.overflowItems,
    testId: testId
  }))) : /*#__PURE__*/React.createElement(Box, {
    role: "list",
    xcss: containerWrapperStyles
  }, /*#__PURE__*/React.createElement(Inline, {
    xcss: [sharedContainerStyles, smallContainerStyles]
  }, filteredItems.length > smallMaxItems &&
  /*#__PURE__*/
  // We don't need to pass items into popup, it won't be interactive (SSR only)
  React.createElement(MoreItemsPopup, {
    moreLabel: moreLabel,
    testId: testId
  })), /*#__PURE__*/React.createElement(Inline, {
    xcss: [sharedContainerStyles, mediumContainerStyles]
  }, navItems.medium.navBarItems, navItems.medium.overflowItems.length > 0 && /*#__PURE__*/React.createElement(MoreItemsPopup, {
    moreLabel: moreLabel,
    testId: testId
  })), /*#__PURE__*/React.createElement(Inline, {
    xcss: [sharedContainerStyles, largeContainerStyles]
  }, navItems.large.navBarItems, navItems.large.overflowItems.length > 0 && /*#__PURE__*/React.createElement(MoreItemsPopup, {
    moreLabel: moreLabel,
    testId: testId
  }))), Create && /*#__PURE__*/React.createElement(Create, null));
});